import { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, ArrowRight, ArrowUpRight, Quote } from "lucide-react";
import { MaskedLines, Reveal } from "../components/Motion";
import { TESTIMONIALS } from "../lib/data";

export default function Testimonials() {
  const [i, setI] = useState(0);
  const next = () => setI((v) => (v + 1) % TESTIMONIALS.length);
  const prev = () => setI((v) => (v - 1 + TESTIMONIALS.length) % TESTIMONIALS.length);
  const t = TESTIMONIALS[i];

  return (
    <div data-testid="testimonials-page" className="pt-32 md:pt-44">
      <section className="max-w-[1400px] mx-auto px-6 md:px-10">
        <p className="overline text-[#C19A6B] mb-6">In their words</p>
        <h1 className="font-serif-lux text-[#1A1A1A] text-[13vw] md:text-[8vw] leading-[0.9] tracking-tight">
          <MaskedLines lines={["Trust, earned", "one deal at", "a time."]} />
        </h1>
      </section>

      {/* Carousel */}
      <section data-testid="testimonial-carousel" className="max-w-[1100px] mx-auto px-6 md:px-10 py-24 md:py-32">
        <Quote size={56} className="text-[#C19A6B] mb-8" />
        <div className="min-h-[280px] md:min-h-[240px]">
          <AnimatePresence mode="wait">
            <motion.blockquote
              key={i}
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
            >
              <p className="font-serif-lux text-3xl md:text-5xl leading-[1.25] text-[#1A1A1A] tracking-tight">
                "{t.quote}"
              </p>
              <footer className="mt-10">
                <p className="font-sans-lux font-semibold text-[#1A1A1A]">{t.name}</p>
                <p className="overline text-[#C19A6B] mt-1">{t.detail}</p>
              </footer>
            </motion.blockquote>
          </AnimatePresence>
        </div>

        <div className="flex items-center gap-4 mt-14 border-t border-[#E5DFD3] pt-8">
          <button data-testid="testimonial-prev" onClick={prev} aria-label="Previous" className="w-12 h-12 border border-[#E5DFD3] flex items-center justify-center hover:bg-[#1A1A1A] hover:text-[#FDFBF7] transition-colors">
            <ArrowLeft size={18} />
          </button>
          <button data-testid="testimonial-next" onClick={next} aria-label="Next" className="w-12 h-12 border border-[#E5DFD3] flex items-center justify-center hover:bg-[#1A1A1A] hover:text-[#FDFBF7] transition-colors">
            <ArrowRight size={18} />
          </button>
          <span className="ml-auto font-sans-lux text-sm text-[#5C5C5C]">
            {String(i + 1).padStart(2, "0")} / {String(TESTIMONIALS.length).padStart(2, "0")}
          </span>
        </div>
      </section>

      {/* Grid of all */}
      <section className="bg-[#F5F2EB] border-y border-[#E5DFD3]">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-24 grid md:grid-cols-3 gap-8">
          {TESTIMONIALS.map((c, idx) => (
            <Reveal key={idx} delay={idx * 0.08}>
              <div className="bg-[#FDFBF7] border border-[#E5DFD3] p-8 h-full">
                <Quote size={28} className="text-[#C19A6B] mb-5" />
                <p className="font-sans-lux text-[#1A1A1A] leading-relaxed">"{c.quote}"</p>
                <p className="font-serif-lux text-2xl text-[#1A1A1A] mt-6">{c.name}</p>
                <p className="overline text-[#5C5C5C] mt-1">{c.detail}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="max-w-[1400px] mx-auto px-6 md:px-10 py-28 text-center">
        <Reveal>
          <h2 className="font-serif-lux text-5xl md:text-7xl text-[#1A1A1A] tracking-tight leading-[0.95]">Be the next success story.</h2>
          <Link to="/contact" data-testid="testimonials-cta" className="inline-flex items-center gap-3 mt-10 border border-[#1A1A1A] text-[#1A1A1A] hover:text-[#FDFBF7] btn-lux px-10 py-5 overline">
            Book a Site Visit <ArrowUpRight size={16} />
          </Link>
        </Reveal>
      </section>
    </div>
  );
}
